import type { BattlepassRewardCell } from '@/hooks/useBattlepass';
import { itemIconFallbackUrl, resolveMediaUrl } from '@/lib/assetUrl';

function genderMatchScore(itemGender: string | undefined, userGender: string): number {
  const g = itemGender ?? 'A';
  if (g === userGender) return 0;
  if (g === 'A') return 1;
  return 2;
}

function dedupeGroupByCodeName(
  items: BattlepassRewardCell[],
  userGender: string,
): BattlepassRewardCell[] {
  const byCode = new Map<string, BattlepassRewardCell>();
  for (const item of items) {
    const key = `${item.CodeName128}_${item.ispremium ?? 0}`;
    const existing = byCode.get(key);
    if (!existing) {
      byCode.set(key, item);
      continue;
    }
    if (genderMatchScore(item.gender, userGender) < genderMatchScore(existing.gender, userGender)) {
      byCode.set(key, item);
    }
  }
  return [...byCode.values()];
}

export function filterGroupItemsByGender(
  items: BattlepassRewardCell[],
  userGender: string | null | undefined,
): BattlepassRewardCell[] {
  const gender = userGender ?? 'A';
  const filtered = items.filter((item) => {
    const itemGender = item.gender ?? 'A';
    return itemGender === 'A' || itemGender === gender;
  });
  return dedupeGroupByCodeName(filtered, gender);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Mismo HTML que appweb_ci4 battlepass.php initBattlepassTooltipsAndPopovers */
export function buildBattlepassGroupPopoverHtml(
  items: BattlepassRewardCell[],
  userGender: string | null | undefined,
): string {
  const filteredItems = filterGroupItemsByGender(items, userGender);
  if (filteredItems.length === 0) return '';

  const fallback = itemIconFallbackUrl();
  let popoverContent = '<div class="bp-group-popover">';
  popoverContent += '<div class="bp-group-popover-items">';

  for (const item of filteredItems) {
    const isDefault = item.is_default === 1;
    const img = escapeHtml(resolveMediaUrl(item.image_url));
    const name = escapeHtml(item.SN_Name || 'Sin nombre');

    popoverContent += `<div class="bp-group-popover-item${isDefault ? ' bp-group-popover-item-default' : ''}">`;
    popoverContent += `<img src="${img}" alt="${name}" class="bp-group-popover-img" onerror="this.src='${fallback}'">`;
    popoverContent += `<div class="bp-group-popover-name">${name}</div>`;
    if (isDefault) {
      popoverContent += '<span class="bp-group-popover-default">⭐</span>';
    }
    popoverContent += '</div>';
  }

  popoverContent += '</div></div>';
  return popoverContent;
}

type BootstrapPopover = {
  dispose: () => void;
};

type BootstrapNS = {
  Popover: {
    new (el: Element, options: Record<string, unknown>): BootstrapPopover;
    getInstance: (el: Element) => BootstrapPopover | null;
  };
};

function getBootstrap(): BootstrapNS | undefined {
  return (window as Window & { bootstrap?: BootstrapNS }).bootstrap;
}

/** Igual que CI4: placement top, trigger hover, container body */
export function initBattlepassGroupPopover(
  el: HTMLElement,
  items: BattlepassRewardCell[],
  userGender: string | null | undefined,
): void {
  const bootstrap = getBootstrap();
  if (!bootstrap?.Popover) return;

  bootstrap.Popover.getInstance(el)?.dispose();

  const content = buildBattlepassGroupPopoverHtml(items, userGender);
  if (!content) return;

  new bootstrap.Popover(el, {
    content,
    html: true,
    placement: 'top',
    trigger: 'hover',
    container: 'body',
    sanitize: false,
  });
}

export function disposeBattlepassGroupPopover(el: HTMLElement): void {
  getBootstrap()?.Popover.getInstance(el)?.dispose();
}

export function waitAndInitBattlepassGroupPopover(
  el: HTMLElement,
  items: BattlepassRewardCell[],
  userGender: string | null | undefined,
): () => void {
  let cancelled = false;
  let attempts = 0;

  const tryInit = () => {
    if (cancelled) return;
    if (getBootstrap()?.Popover) {
      initBattlepassGroupPopover(el, items, userGender);
      return;
    }
    attempts += 1;
    if (attempts < 30) {
      window.setTimeout(tryInit, 100);
    }
  };

  tryInit();

  return () => {
    cancelled = true;
    disposeBattlepassGroupPopover(el);
  };
}
